import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { blogPosts } from '../data/blog';
import { Dog, Calendar, Clock, ArrowRight, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';

const BlogPage = () => {
  const featuredPost = blogPosts[0];
  const otherPosts = blogPosts.slice(1);

  return (
    <>
      <Helmet>
        <title>Dog Care Blog | Dog Boarding Baltimore - Tips, Guides & Local Info</title>
        <meta name="description" content="Expert dog care tips, Baltimore pet guides, and advice from Dog Boarding Baltimore. Learn about dog boarding, daycare, training, and the best pet-friendly spots in Baltimore." />
        <link rel="canonical" href="https://dogboardingbaltimore.com/blog" />
        
        <meta property="og:title" content="Dog Care Blog | Dog Boarding Baltimore" />
        <meta property="og:description" content="Expert dog care tips and Baltimore pet guides from Dog Boarding Baltimore." />
        <meta property="og:url" content="https://dogboardingbaltimore.com/blog" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Dog Boarding Baltimore Blog",
            "description": "Expert dog care tips, Baltimore pet guides, and advice from Dog Boarding Baltimore",
            "url": "https://dogboardingbaltimore.com/blog",
            "publisher": {
              "@type": "Organization",
              "name": "Dog Boarding Baltimore",
              "url": "https://dogboardingbaltimore.com"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 md:h-20">
              <Link to="/" className="flex items-center gap-2 group">
                <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center group-hover:bg-amber-600 transition-colors">
                  <Dog className="w-6 h-6 text-white" />
                </div>
                <div className="hidden sm:block">
                  <span className="font-bold text-lg text-stone-800">Dog Boarding</span>
                  <span className="block text-xs text-amber-600 font-medium -mt-1">Baltimore</span>
                </div>
              </Link>
              <div className="flex items-center gap-4">
                <Link to="/" className="text-stone-600 hover:text-amber-600 font-medium text-sm hidden sm:block">
                  Home
                </Link>
                <Link to="/">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-2 rounded-lg">
                    Get Free Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="pt-24 md:pt-28 pb-12 bg-gradient-to-b from-amber-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <span className="inline-block bg-amber-100 text-amber-700 font-semibold text-sm px-4 py-1.5 rounded-full mb-4">
                Our Blog
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4">
                Dog Care Tips & Baltimore Guides
              </h1>
              <p className="text-lg text-stone-600">
                Expert advice on dog boarding, pet care, and the best spots for dogs in Baltimore. From preparation tips to local guides – everything Baltimore dog owners need to know.
              </p>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to={`/blog/${featuredPost.slug}`} className="group block">
              <article className="grid md:grid-cols-2 gap-8 bg-stone-50 rounded-2xl overflow-hidden border border-stone-100 hover:border-amber-200 hover:shadow-xl transition-all">
                <div className="aspect-video md:aspect-auto overflow-hidden">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1 rounded-full">
                      Featured
                    </span>
                    <span className="text-stone-500 text-sm flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {featuredPost.readTime}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4 group-hover:text-amber-600 transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-stone-600 mb-6 line-clamp-3">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-stone-500">
                      <Calendar className="w-4 h-4" />
                      {new Date(featuredPost.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                    <span className="text-amber-600 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read More
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </article>
            </Link>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-12 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-stone-800 mb-8">More Articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherPosts.map((post) => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="group block">
                  <article className="bg-white rounded-xl overflow-hidden border border-stone-100 hover:border-amber-200 hover:shadow-lg transition-all h-full flex flex-col">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-amber-600 text-xs font-semibold">
                          {post.category}
                        </span>
                        <span className="text-stone-400">•</span>
                        <span className="text-stone-500 text-xs">{post.readTime}</span>
                      </div>
                      <h3 className="text-lg font-bold text-stone-800 mb-3 group-hover:text-amber-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-stone-600 text-sm mb-4 line-clamp-2 flex-grow">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-auto">
                        <span className="text-sm text-stone-500">
                          {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span className="text-amber-600 font-medium text-sm flex items-center gap-1">
                          Read
                          <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-amber-500">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Need Dog Boarding in Baltimore?
            </h2>
            <p className="text-lg text-amber-100 mb-8">
              Trusted by Baltimore families for 5+ years. Background-checked staff, daily updates, and lots of love.
            </p>
            <Link to="/">
              <Button className="bg-white text-amber-600 hover:bg-amber-50 font-bold px-8 py-6 text-lg rounded-xl">
                Get Your Free Quote
              </Button>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-stone-900 text-stone-400 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                  <Dog className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-white">Dog Boarding Baltimore</span>
              </Link>
              <p className="text-sm">
                © {new Date().getFullYear()} Dog Boarding Baltimore. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPage;
